import InfoTooltip from '@components/InfoTooltip';
import { isNumber } from 'lodash';
import { ReactElement, ReactNode } from 'react';
import { Trans } from 'react-i18next';
import { useCurrency } from 'src/hooks/useCurrency';
import { SupportedCurrencyCode } from 'src/modules/currency';

import { percentFormat } from '../format';
import { CarsTableNextItem } from '../types';

import { DeltaChip } from './DeltaChip';
import { NumericCell } from './NumericCell';

/**
 * The backend rounds `priceChange.relative` to whole percentage points, so anything below half a
 * percent of the first price formats as "0 %". Badging that would contradict the amount next to it.
 */
const MIN_RELATIVE_PRICE_CHANGE = 0.005;

const cellStyle = { display: 'flex', alignItems: 'center', gap: 4 } as const;

export const getPriceChange = (original: CarsTableNextItem) => {
  const first = original.priceFirst;
  const current = original.priceFixed;
  const amount = original.priceChange?.absolute?.amount;

  if (first?.value == null || current?.value == null || !isNumber(amount) || amount === 0) {
    return null;
  }

  return { first, current, amount, relative: original.priceChange?.relative };
};

const PriceChangeInfo = ({ original }: { original: CarsTableNextItem }) => {
  const { format, convert } = useCurrency();
  const change = getPriceChange(original);

  if (!change) {
    return null;
  }

  return (
    <InfoTooltip
      xsIcon
      placement="left"
      hint={
        <Trans
          i18nKey="carsTableNext.columns.priceChange-tooltip"
          values={{
            first: format(
              convert({ amount: change.first.value, currency: change.first.unit as SupportedCurrencyCode }, {}),
              {},
            ),
            current: format(
              convert({ amount: change.current.value, currency: change.current.unit as SupportedCurrencyCode }, {}),
              {},
            ),
          }}
        />
      }
    />
  );
};

/**
 * Lays the price change cells out as info icon (left) | chip (centred) | value (right). The spacer
 * is always rendered so the value stays pinned right even when there is no chip.
 */
const adornment = (original: CarsTableNextItem, chip?: ReactNode) => (
  <>
    <PriceChangeInfo original={original} />
    <span style={{ flex: 1, textAlign: 'center' }}>{chip}</span>
  </>
);

export const PriceChangeAbsoluteCell = ({ original }: { original: CarsTableNextItem }): ReactElement => {
  const change = getPriceChange(original);
  // Gated on the percentage the chip actually renders, not on the amount, so a sub-percent change
  // shows its value without a badge claiming "0 %".
  const chip =
    change && isNumber(change.relative) && Math.abs(change.relative) >= MIN_RELATIVE_PRICE_CHANGE ? (
      <DeltaChip value={change.amount} label={percentFormat.format(change.relative)} />
    ) : undefined;

  return (
    <NumericCell
      value={original.priceChange?.absolute ?? '-'}
      style={cellStyle}
      startAdornment={adornment(original, chip)}
    />
  );
};

export const PriceChangeRelativeCell = ({ original }: { original: CarsTableNextItem }): ReactElement => (
  <NumericCell
    value={original.priceChange?.relative ?? '-'}
    format={percentFormat.format}
    style={cellStyle}
    startAdornment={adornment(original)}
  />
);
